import { Component, OnInit } from "@angular/core";
import { GameControlsBase } from "../game-controls.base";
import { AuthService } from "../../../../auth/auth.service";
import { MainService } from "../../../main.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { ManagerService } from "../../../../core/services/manager.service";
import { DataManagerService } from "../../../../core/services/data-manager.service";
import {
  LiveLobbyTournament,
  Settings,
  TournamentInformation,
} from "../../../../shared/models";
import { Observable, Subject, combineLatest, merge, of } from "rxjs";
import {
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { environment } from "../../../../../environments/environment";
import { ControlActions } from "../game-controls-enum";
import { AssetsLoaderService } from "../../../../core/services/assets-loader.service";
import { ConfigService } from "../../../../core/services/config.service";
import { v3ManagerService } from "../../../../core/services/v3-manager.service";
import { Limit, VariantType2 } from "../../../../shared/enums/poker-types";
import { MatDialog } from "@angular/material/dialog";
import { FallGuysComponent } from "../../dialogs/fall-guys/fall-guys.component";

@Component({
  selector: "app-game-controls",
  templateUrl: "./game-controls.component.html",
  styleUrls: ["./game-controls.component.scss"],
})
export class GameControlsComponent extends GameControlsBase implements OnInit {
  env = environment;
  handsetPortrait = false;
  handsetLandscape = false;

  showChatBox = false;

  settings: Settings;
  destroy$ = new Subject<boolean>();
  buyChipsUrl: string;
  config;
  showRoundedItems = false; //dutchpokeracademy brand uses rounded items
  /** CDN table action icons (replay / sit out / chat) — no button background */
  useSkinTableIcons = false;
  skinTableIcons = {
    replay: "",
    sitOut: "",
    chat: "",
  };

  tournamentInfo$: Observable<TournamentInformation>;
  variantType2 = VariantType2;
  limitType = Limit;

  spellZone$: Observable<any>;
  myPosition: number;

  constructor(
    public authService: AuthService,
    public mainService: MainService,
    private breakpointObserver: BreakpointObserver,
    public managerService: ManagerService,
    private dataManager: DataManagerService,
    public assetsLoader: AssetsLoaderService,
    private dialog: MatDialog,
    private configService: ConfigService,
    private _v3ManagerService: v3ManagerService,
  ) {
    super();
    this.config = this.configService.config;
    this.showRoundedItems =
      this.configService.config?.brand === "dutchpokeracademy" ||
      this.configService.config?.brand === "sc"; //dutchpokeracademy brand uses rounded items
    this.useSkinTableIcons = this.config?.code === "rivercard-jwso";
    this.setSkinTableIconUrls();

    const v3ActiveTournament$ = this.managerService.activeGames$.pipe(
      map((activeGames) =>
        Object.values(activeGames).find((el) => el.isActive),
      ),
      switchMap((activeGame) => {
        if (activeGame?.table?.IdTournament) {
          return this._v3ManagerService.getTournament(
            activeGame.table.IdTournament,
          );
        }
        return of(undefined);
      }),
    );

    const liveLobbyByTournamentId$ = merge(
      of({} as Record<number, LiveLobbyTournament>),
      this.dataManager.messageReceivedObservable.pipe(
        filter(
          (msg: any) =>
            msg?.Response === "LiveLobbyTournament" && msg.LiveLobbyTournament,
        ),
        map((msg: any) => msg.LiveLobbyTournament as LiveLobbyTournament),
        scan(
          (acc, live) => ({ ...acc, [live.IdTournament]: live }),
          {} as Record<number, LiveLobbyTournament>,
        ),
      ),
    ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.tournamentInfo$ = combineLatest([
      v3ActiveTournament$,
      liveLobbyByTournamentId$,
    ]).pipe(
      map(([tournament, liveById]) => {
        if (!tournament?.tourSum?.Id) {
          return tournament;
        }
        const live = liveById[tournament.tourSum.Id];
        if (!live) {
          return tournament;
        }
        return {
          ...tournament,
          tourSum: {
            ...tournament.tourSum,
            NextBlind: live.NextBlind,
            NextAnte: live.NextAnte,
            Blind: live.Blind,
            Ante: live.Ante,
            NbPlayersActive: live.NbPlayersActive,
            NbPlayerRegistered: live.NbPlayersRegistered,
            BlindIncreaseIn: live.BlindIncreaseIn,
            BreakIn: live.BreakIn,
            ReBuyEndIn: live.ReBuyEndIn,
          },
        } as TournamentInformation;
      }),
    );
    this.spellZone$ = merge(
      this.tournamentInfo$.pipe(
        map(
          (tour) =>
            (tour as any)?.tourSum?.BountySpellConfiguration
              ?.MinPlayersRequredToActivateSpell,
        ),

        filter((data) => data > 0),
      ),

      this.managerService.activeGames$.pipe(
        map((activeGames) =>
          Object.values(activeGames).find((el) => el.isActive),
        ),
        switchMap((activeGame) => {
          if (activeGame?.table?.IdTournament) {
            return this.managerService.spellZones$.pipe(
              map((zones) => zones[activeGame?.table?.IdTournament]),
            );
          }
          {
            return of(undefined);
          }
        }),
        map((data) => data?.minActivePlayersRequired),
        tap((data) => console.log("++++spellZone2", data)),

        filter((data) => data > 0),
      ),
    );

    this.managerService.viewTournament$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (viewingTournament) => {
          console.log("++++viewingTournament NEA", viewingTournament);
          this.myPosition = viewingTournament?.myPosition;
        },
      });
  }

  private setSkinTableIconUrls(): void {
    if (!this.useSkinTableIcons) {
      return;
    }
    const base = `${environment.dataSource}/assets/${this.config.code}/tables`;
    this.skinTableIcons = {
      replay: `${base}/replay.png`,
      sitOut: `${base}/SitOut.png`,
      chat: `${base}/table_chat.png`,
    };
  }

  ngOnInit() {
    const theme = localStorage.getItem("theme");
    if (theme) {
      this.buyChipsUrl = `assets/theme/${theme}/tables/buy_chips.png`;
    } else {
      this.buyChipsUrl = `${environment.dataSource}/assets/${this.config.code}/tables/buy_chips.png`;
    }
    this.assetsLoader.reloadObservable$.subscribe(() => {
      const theme = localStorage.getItem("theme");
      if (theme) {
        this.buyChipsUrl = `assets/theme/${theme}/tables/buy_chips.png`;
      } else {
        this.buyChipsUrl = `${environment.dataSource}/assets/${this.config.code}/tables/buy_chips.png`;
      }
      this.setSkinTableIconUrls();
    });

    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait])
      .subscribe((result) => {
        this.handsetPortrait = result.matches;
      });

    this.breakpointObserver
      .observe([Breakpoints.HandsetLandscape])
      .subscribe((result) => {
        this.handsetLandscape = result.matches;
      });

    this.managerService.settings$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (settings) => {
        this.settings = settings;
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  callTournaments(idTournament: number) {
    this.managerService.tournamentView(
      this.managerService.getTournament(idTournament).tourSum,
    );
  }

  clickOpenFallGuysLevelsForApps(data: any) {
    this.dialog.open(FallGuysComponent, {
      data,
      width: "360px",
      //position: { bottom: '100px' }
    });
  }
}
